import apiService from './apiService';
import { GET, POST, PUT, PATCH, DELETE } from './executer';

/**
 * Configuration consumers - systems that use this config
 */
export type ConfigConsumer = 'backend' | 'jobs' | 'client' | 'scheduler' | 'notification-service' | 'general';

/**
 * All available config consumers
 */
export const ALL_CONFIG_CONSUMERS: ConfigConsumer[] = [
  'backend',
  'jobs',
  'client',
  'scheduler',
  'notification-service',
  'general',
];

/**
 * Value types for configuration values
 */
export type ConfigValueType = 'string' | 'number' | 'boolean' | 'json';

/**
 * System Configuration entity from the database
 */
export interface SystemConfig {
  config_key: string;
  /** Null when the admin list/get redacted the value */
  config_value: string | null;
  /** Systems that consume this configuration */
  consumers: ConfigConsumer[];
  sensitive: boolean;
  description: string | null;
  value_type: ConfigValueType;
  updated_at: string;
  updated_by: string;
  created_at: string;
  created_by: string;
  deleted?: boolean;
  value_redacted?: boolean;
}

/**
 * Request payload for creating a new configuration
 */
export interface CreateSystemConfigRequest {
  config_key: string;
  config_value: string;
  /** Systems that consume this configuration */
  consumers: ConfigConsumer[];
  sensitive?: boolean;
  description?: string;
  value_type?: ConfigValueType;
}

/**
 * Request payload for updating a configuration
 */
export interface UpdateSystemConfigRequest {
  config_value?: string;
  /** Systems that consume this configuration */
  consumers?: ConfigConsumer[];
  sensitive?: boolean;
  description?: string;
  value_type?: ConfigValueType;
}

/**
 * Response from the system config API
 */
export interface SystemConfigsResponse {
  success: boolean;
  data: SystemConfig[];
  count: number;
}

export interface SystemConfigResponse {
  success: boolean;
  data: SystemConfig;
}

/**
 * Helper function to parse config value based on type
 */
export function parseConfigValue(value: string, valueType: ConfigValueType): string | number | boolean | object {
  switch (valueType) {
    case 'number':
      return parseFloat(value);
    case 'boolean':
      return value.toLowerCase() === 'true';
    case 'json':
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    default:
      return value;
  }
}

class SystemConfigService {
  /**
   * Get all system configurations (admin only)
   * @param consumer - Optional filter by consumer
   * @param includeDeleted - If true, includes soft-deleted configs (for management UI)
   */
  async getAllConfigs(token: string, consumer?: ConfigConsumer, includeDeleted?: boolean): Promise<SystemConfig[]> {
    try {
      const params = new URLSearchParams();
      if (consumer) params.set('consumer', consumer);
      if (includeDeleted) params.set('include_deleted', 'true');
      const query = params.toString();
      const url = query ? `/api/config?${query}` : '/api/config';
      
      const response = await apiService.client.execute<SystemConfig[]>(
        GET,
        url,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch system configurations');
    } catch (error) {
      console.error('Error fetching system configurations:', error);
      throw error;
    }
  }

  /**
   * Get a configuration by key
   */
  async getConfigByKey(configKey: string, token: string): Promise<SystemConfig> {
    try {
      const response = await apiService.client.execute<SystemConfig>(
        GET,
        `/api/config/${encodeURIComponent(configKey)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(`Failed to fetch configuration: ${configKey}`);
    } catch (error) {
      console.error(`Error fetching configuration ${configKey}:`, error);
      throw error;
    }
  }

  /**
   * Create a new configuration
   * Requires: root role
   */
  async createConfig(configData: CreateSystemConfigRequest, token: string): Promise<SystemConfig> {
    const response = await apiService.client.execute<SystemConfig>(
      POST,
      '/api/config',
      {
        data: configData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.errorData?.message || 'Failed to create configuration');
  }

  /**
   * Update an existing configuration
   * Requires: root role
   */
  async updateConfig(
    configKey: string,
    updates: UpdateSystemConfigRequest,
    token: string
  ): Promise<SystemConfig> {
    const response = await apiService.client.execute<SystemConfig>(
      PUT,
      `/api/config/${encodeURIComponent(configKey)}`,
      {
        data: updates,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.errorData?.message || 'Failed to update configuration');
  }

  /**
   * Set the deleted flag on a configuration (soft-delete or restore)
   * Requires: root role
   */
  async setConfigDeleted(configKey: string, deleted: boolean, token: string): Promise<void> {
    const response = await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      `/api/config/${encodeURIComponent(configKey)}/deleted`,
      {
        data: { deleted },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    if (!response.success) {
      throw new Error(response.errorData?.message || 'Failed to update configuration deleted flag');
    }
  }

  /**
   * Soft-delete a configuration
   * Requires: root role
   */
  async deleteConfig(configKey: string, token: string): Promise<void> {
    const response = await apiService.client.execute<{ message: string }>(
      DELETE,
      `/api/config/${encodeURIComponent(configKey)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    if (!response.success) {
      throw new Error(response.errorData?.message || 'Failed to delete configuration');
    }
  }

  /**
   * Rename a configuration key
   * Requires: root role
   */
  async renameConfig(oldKey: string, newKey: string, token: string): Promise<SystemConfig> {
    const response = await apiService.client.execute<SystemConfig>(
      PATCH,
      `/api/config/${encodeURIComponent(oldKey)}/rename`,
      {
        data: { new_key: newKey },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.errorData?.message || 'Failed to rename configuration');
  }

  /**
   * Reveal one config value after re-entering the admin's Tomorrow.io password.
   * Requires: root role
   */
  async revealConfig(
    configKey: string,
    password: string,
    recaptchaToken: string,
    token: string
  ): Promise<SystemConfig> {
    const response = await apiService.client.execute<SystemConfig>(
      POST,
      `/api/config/${encodeURIComponent(configKey)}/reveal`,
      {
        data: { password, recaptchaToken },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.errorData?.message || 'Failed to reveal configuration');
  }

  /**
   * Get public client configurations (no auth required)
   */
  async getPublicConfigs(): Promise<Record<string, string | number | boolean | object>> {
    try {
      const response = await apiService.client.execute<Record<string, string | number | boolean | object>>(
        GET,
        '/api/config/public'
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch public configurations');
    } catch (error) {
      console.error('Error fetching public configurations:', error);
      throw error;
    }
  }
}

export const systemConfigService = new SystemConfigService();
export default systemConfigService;

